import queryString from "query-string";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useFetchProductsByReleaseIdQuery } from "../../../api/products/hooks/usefetchProductsByReleaseIdQuery";
import { useFetchReleaseQuery } from "../../../api/releases/hooks/useFetchReleaseQuery";
import {
  CreateReleaseFlow,
  ReleaseStatus,
} from "../../../store/models/release";
import {
  getCreateReleaseRoute,
  getEditReleaseScreenRoute,
} from "../../../store/utils/routeGetters";

/**
 * Handles the edit release action (for Scheduled Projects).
 * Navigates user to either the Create Release or Edit Release screen.
 * @param releaseId The ID of the release to edit
 * @returns A function that handles the edit release action
 */
export const useHandleEditRelease = (releaseId: number | undefined) => {
  const navigate = useNavigate();
  // release details has more info than release (i.e. draft status and product types)
  const { data: releaseDetails } = useFetchReleaseQuery(releaseId);
  const { data: products } = useFetchProductsByReleaseIdQuery(releaseId);
  const isDraft = useMemo(
    () => releaseDetails?.status === ReleaseStatus.DRAFT,
    [releaseDetails?.status],
  );
  const isDTC = useMemo(
    () => Boolean(releaseDetails?.product_types?.includes("DTC")),
    [releaseDetails?.product_types],
  );

  const handleEditRelease = useCallback(() => {
    if (!releaseId) return;

    // Go to edit release screen
    if (!isDraft) {
      navigate(getEditReleaseScreenRoute(releaseId));
      return;
    }

    // Go to create free release screen
    if (!isDTC) {
      navigate(getCreateReleaseRoute(releaseId, CreateReleaseFlow.FREE));
      return;
    }

    // Go to create DTC release screen
    const search = `?${queryString.stringify(
      {
        stepper: 0,
        product_id: products?.[0]?.id,
      },
      { skipEmptyString: true, skipNull: true },
    )}`;

    navigate({
      pathname: getCreateReleaseRoute(releaseId, CreateReleaseFlow.DTC),
      search,
    });
  }, [releaseId, isDraft, isDTC, products, navigate]);

  return handleEditRelease;
};
